import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertTriangle, Clock, Info, Plane, TrainFront, ClipboardList, RefreshCw, Radar, ListChecks, History, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { Counter, Panel, fmtDT } from "../components/sentinel/shared";
import { TravelTable } from "../components/sentinel/TravelTable";
import { AnomalyList } from "../components/sentinel/AnomalyList";

const INTERNAL = ["missing_driver", "missing_vehicle", "conflict_driver", "conflict_vehicle", "missing_data", "travel_id", "stale_in_progress", "cancelled_operative", "delay_risk"];

function Header({ data, running, onRun }) {
  const lr = data.last_run;
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl text-white flex items-center gap-3 tracking-wide"><Radar className="text-amber-400" />SENTINEL</h1>
        <p className="text-[11px] uppercase tracking-[0.3em] text-amber-300/80 mt-1">Intelligent Operations Control</p>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right text-xs" data-testid="sentinel-last-run">
          <div className="flex items-center gap-1.5 justify-end text-slate-200"><span className={`w-2 h-2 rounded-full ${data.last_error ? "bg-rose-400" : lr ? "bg-emerald-400" : "bg-slate-500"}`} />
            {data.last_error ? "Ultimo controllo con errore" : lr ? "Sistema operativo" : "Nessun controllo ancora eseguito"}</div>
          <div className="text-slate-500">Ultimo controllo: {lr ? fmtDT(lr.finished_at) : "—"} · {data.schedule}</div>
        </div>
        <button type="button" disabled={running} onClick={onRun} data-testid="sentinel-run-now"
          className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold bg-gradient-to-b from-[#F1D27A] to-[#C99A24] text-black disabled:opacity-60">
          <RefreshCw size={15} className={running ? "animate-spin" : ""} />{running ? "Controllo..." : "Aggiorna ora"}
        </button>
      </div>
    </div>
  );
}

function SourceNotice({ sources }) {
  return (
    <div className="rounded-xl border border-slate-700/70 bg-slate-900/30 p-3 text-xs text-slate-400 flex gap-2" data-testid="sentinel-source-notice">
      <CloudOff size={15} className="text-slate-500 shrink-0 mt-0.5" />
      <div><span className="text-slate-200 font-semibold">Fonti esterne.</span> Treni: {sources.train.reason}{sources.train.last_errors?.length ? <span className="text-rose-300" data-testid="sentinel-rail-error"> Ultimo controllo: {sources.train.last_errors.join("; ")}.</span> : ""} Voli: fonte non disponibile. {sources.flight.reason} Sentinel non mostra mai ritardi non confermati dalla fonte.</div>
    </div>
  );
}

function InternalSummary({ byType, labels, onPick }) {
  return (
    <div className="grid gap-1" data-testid="sentinel-internal-summary">
      {INTERNAL.map((t) => (
        <button key={t} type="button" onClick={() => onPick(t)} data-testid={`internal-check-${t}`} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-amber-500/5 text-left">
          <span className={`font-display text-xl w-8 ${byType[t] ? "text-amber-300" : "text-slate-600"}`}>{byType[t] || 0}</span>
          <span className="text-sm text-slate-300">{labels[t]}</span>
        </button>
      ))}
    </div>
  );
}

export default function Sentinel() {
  const [data, setData] = useState(null);
  const [hist, setHist] = useState(null);
  const [tab, setTab] = useState("anomalie");
  const [typeFilter, setTypeFilter] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try { setData((await api.get("/sentinel/overview")).data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "storico") { api.get("/sentinel/history").then((r) => setHist(r.data.items)).catch((e) => toast.error(formatApiError(e))); }
  }, [tab]);

  const run = async () => {
    setRunning(true);
    try {
      const r = (await api.post("/sentinel/run")).data;
      toast.success(`Controllo completato: ${r.detected} eccezioni rilevate, ${r.created} nuove, ${r.auto_closed} non più pertinenti`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setRunning(false);
  };
  const setStatus = async (a, status) => {
    try { await api.patch(`/sentinel/anomalies/${a.id}`, { status }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const anomalies = useMemo(() => (data?.anomalies || []).filter((a) => !typeFilter || typeFilter.includes(a.type)), [data, typeFilter]);

  if (!data) return <div className="text-slate-500 text-sm p-10 text-center">Caricamento...</div>;
  const c = data.counts;
  const toAssign = (data.by_type.missing_driver || 0) + (data.by_type.missing_vehicle || 0);
  const go = (t, f = null) => { setTab(t); setTypeFilter(f); };
  return (
    <div className="space-y-5" data-testid="sentinel-page">
      <Header data={data} running={running} onRun={run} />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Counter icon={AlertTriangle} label="Anomalie alte" hint="Richiedono attenzione" value={c.alta} tone="text-rose-400" testid="sentinel-count-alta" onClick={() => go("anomalie")} />
        <Counter icon={Clock} label="Da verificare" hint="Possibile impatto" value={c.media} tone="text-amber-300" testid="sentinel-count-media" onClick={() => go("anomalie")} />
        <Counter icon={Info} label="Informative" hint="Nessun intervento immediato" value={c.info} tone="text-sky-300" testid="sentinel-count-info" onClick={() => go("anomalie")} />
        <Counter icon={Plane} label="Voli collegati" hint={`Prossimi ${data.horizon_days} giorni`} value={data.flights.length} tone="text-white" testid="sentinel-count-flights" onClick={() => go("voli")} />
        <Counter icon={TrainFront} label="Treni collegati" hint={`Prossimi ${data.horizon_days} giorni`} value={data.trains.length} tone="text-white" testid="sentinel-count-trains" onClick={() => go("treni")} />
        <Counter icon={ClipboardList} label="Da assegnare" hint="Senza autista o veicolo" value={toAssign} tone="text-amber-200" testid="sentinel-count-unassigned" onClick={() => go("anomalie", ["missing_driver", "missing_vehicle"])} />
      </div>
      <SourceNotice sources={data.sources} />
      <Tabs value={tab} onValueChange={(v) => go(v)}>
        <TabsList className="bg-[#0A0907] border border-amber-500/20 flex-wrap h-auto">
          {[["anomalie", "Anomalie", AlertTriangle], ["voli", "Voli", Plane], ["treni", "Treni", TrainFront], ["interni", "Controlli interni", ListChecks], ["storico", "Storico", History]].map(([v, l, I]) => (
            <TabsTrigger key={v} value={v} data-testid={`sentinel-tab-${v}`} className="gap-1.5 data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200"><I size={14} />{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="anomalie">
          <Panel title="Centro anomalie" icon={AlertTriangle} subtitle="Solo le eccezioni che richiedono attenzione, aggiornate a ogni controllo" testid="sentinel-anomalies-panel"
            right={typeFilter && <button type="button" onClick={() => setTypeFilter(null)} data-testid="sentinel-clear-filter" className="text-xs text-amber-300">{typeFilter.map((t) => data.type_labels[t]).join(" + ")} · mostra tutte</button>}>
            <AnomalyList items={anomalies} labels={data.type_labels} statuses={data.statuses} onStatus={setStatus} empty="Nessuna anomalia aperta" />
          </Panel>
        </TabsContent>
        <TabsContent value="voli"><Panel title="Voli collegati alle prenotazioni" icon={Plane} subtitle="Numero volo letto dalla prenotazione originale" testid="sentinel-flights-panel"><TravelTable rows={data.flights} kind="flight" /></Panel></TabsContent>
        <TabsContent value="treni"><Panel title="Treni collegati alle prenotazioni" icon={TrainFront} subtitle="Numero treno letto dalla prenotazione originale" testid="sentinel-trains-panel"><TravelTable rows={data.trains} kind="train" /></Panel></TabsContent>
        <TabsContent value="interni"><Panel title="Controlli interni" icon={ListChecks} subtitle="Basati solo sui dati del gestionale, indipendenti da fonti esterne" testid="sentinel-internal-panel"><InternalSummary byType={data.by_type} labels={data.type_labels} onPick={(t) => go("anomalie", [t])} /></Panel></TabsContent>
        <TabsContent value="storico"><Panel title="Storico anomalie" icon={History} subtitle="Anomalie risolte o non più pertinenti" testid="sentinel-history-panel">
          {!hist ? <div className="text-sm text-slate-500">Caricamento...</div> : <AnomalyList items={hist} labels={data.type_labels} statuses={data.statuses} readOnly empty="Nessuna anomalia chiusa" />}
        </Panel></TabsContent>
      </Tabs>
    </div>
  );
}
